import * as React from "react";
import { Root, createRoot } from "react-dom/client";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
// import { LS2PACTheme, LS2KidsTheme, LS2ThemeProvider } from "./Theme";
import { LS2ThemeProvider } from "./Theme";
import { LanguageProvider, Translations } from "./Language";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import "dayjs/locale/en";
import "dayjs/locale/en-ca";
import "dayjs/locale/en-gb";
import "dayjs/locale/es";
import "dayjs/locale/es-mx";
import "dayjs/locale/es-pr";
import "dayjs/locale/es-us";
import "dayjs/locale/fa";
import "dayjs/locale/fr";
import "dayjs/locale/fr-ca";
import "dayjs/locale/uk";
import "dayjs/locale/zh";
import "dayjs/locale/zh-cn";
const supportedLocales = new Set([
  "en",
  "en-ca",
  "en-gb",
  "es",
  "es-mx",
  "es-pr",
  "es-us",
  "fa",
  "fr",
  "fr-ca",
  "uk",
  "zh",
  "zh-cn",
]);
// import {
//   Experimental_CssVarsProvider as CssVarsProvider,
// } from '@mui/material/styles';
import { CssBaseline } from "@mui/material";
import { LocationConfigurationProvider } from "./LocationConfiguration";

const localeOverrides: Record<string, string> = {
  "fr-us": "fr-ca",
  "cn-us": "zh-cn",
};

// https://day.js.org/docs/en/i18n/i18n
export const getAdapterLocale = () => {
  const htmlLangAttribute = document.documentElement.lang;
  console.log(htmlLangAttribute); // This will log the value of the lang attribute

  const normalized = htmlLangAttribute.trim().toLowerCase().replace(/_/g, "-");
  const [language, region] = normalized.split("-");

  if (localeOverrides[normalized]) {
    return localeOverrides[normalized];
  } else if (supportedLocales.has(normalized)) {
    return normalized;
  } else if (supportedLocales.has(language)) {
    return language;
  } else {
    return "en";
  }
};

export function unmountFromShadow(root: Root, container: Element) {
  root.unmount();
  const shadowContainer = container.shadowRoot;
  if (shadowContainer) {
    shadowContainer.innerHTML = "";
  }
}

const sharedCssCache = createCache({
  key: "css",
});

export function renderWithoutShadow(
  children: React.JSX.Element,
  container: Element,
  translations: Translations,
  isKids: boolean = false
): Root | null {
  if (!container) {
    console.error("renderWithoutShadow requires a valid container element");
    console.trace();
    return;
  }

  const root = createRoot(container);

  root.render(
    <CacheProvider value={sharedCssCache}>
      <CssBaseline />
      {/* RAIL-500 Because there were multiple ScopedCssBaseline we were having issues with resets being applied after MUI component styles (box-sixing especially) */}
      {/* <ScopedCssBaseline sx={{backgroundColor: "inherit"}}> */}
      <LocationConfigurationProvider>
        <LS2ThemeProvider isKids={isKids}>
          <LanguageProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={getAdapterLocale()}
            >
              {children}
            </LocalizationProvider>
          </LanguageProvider>
        </LS2ThemeProvider>
      </LocationConfigurationProvider>
      {/* </ScopedCssBaseline> */}
    </CacheProvider>
  );

  return root;
}

export function renderInShadow(
  children: React.JSX.Element,
  container: Element,
  translations: Translations
): Root | null {
  if (!container) {
    console.error("renderInShadow requires a valid container element");
    console.trace();
    return;
  }

  const shadowContainer =
    container.shadowRoot || container.attachShadow({ mode: "open" });

  const cache = createCache({
    key: "css",
    prepend: true,
    container: shadowContainer,
  });

  const root = createRoot(shadowContainer);

  root.render(
    <CacheProvider value={cache}>
      <CssBaseline />
      {/* <ScopedCssBaseline sx={{backgroundColor: "inherit"}}> */}
      <LocationConfigurationProvider>
        <LS2ThemeProvider isKids={false}>
          <LanguageProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={getAdapterLocale()}
            >
              {children}
            </LocalizationProvider>
          </LanguageProvider>
        </LS2ThemeProvider>
      </LocationConfigurationProvider>
      {/* </ScopedCssBaseline> */}
    </CacheProvider>
  );

  return root;
}
